import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSession } from "../app/session";
import { useEncryptionMode, useSpaceKey } from "../app/encryption";
import { AuthShell } from "../components/AuthShell";
import { Button, Card } from "../components/ui";
import { CopyIcon } from "../components/icons";
import { ApiError, api } from "../lib/api";
import { enableEncryption } from "../lib/encryption";
import { syncEngine } from "../lib/sync";
import { cn } from "../lib/cn";
import { useT } from "../i18n/index.js";

/**
 * Единственный разговор про шифрование, который человек не пропустит.
 *
 * Он случается сразу после того, как человек завёл СВОЁ пространство по коду
 * «в приложение», и ровно один раз. Момент выбран не из вежливости: пространство
 * пустое, и включение здесь не стоит ничего — догонять нечем. Через неделю в нём
 * лежит переписка, через год — годы переписки, и каждый день ожидания добавляет
 * записей, которые придётся перешифровывать. Карточка в настройках никуда не
 * делась, но её не находят.
 *
 * Разговор идёт двумя половинами, и обе честные. Не пугать и не уговаривать:
 * у «оставить как есть» есть своя выгода — потерянный телефон ничего не стирает,
 * — а у «включить» есть своя цена, и она названа словом «обязанность». Выхода
 * два, оба ведут в приложение, и ни один не написан крупнее другого.
 *
 * Экрана нет для того, кто вошёл в чужое пространство по коду «в пространство»:
 * режим там задан владельцем, и предлагать такому человеку включение — предлагать
 * невозможное.
 */

/** Какой из шагов сейчас на экране. */
type Stage = "offer" | "code" | "invite";

/**
 * Намерение показать этот экран — на одну навигацию, не дольше.
 *
 * Это событие регистрации, а не свойство учётной записи, поэтому на сервере ему
 * места нет. `sessionStorage` — потому что вкладка, в которой регистрировались,
 * и есть весь срок жизни намерения: закрытая вкладка уносит его с собой, а
 * перезагрузка не находит — экран уже забрал отметку себе (см. `claimOffer`).
 */
const OFFER_KEY = "vmeste.welcome";

/** Зовётся с экрана регистрации, когда заведено своё пространство. */
export function offerEncryptionAfterRegister(): void {
  try {
    sessionStorage.setItem(OFFER_KEY, "1");
  } catch {
    /* хранилище недоступно — экран просто не покажется, и это не поломка */
  }
}

function claimOffer(): boolean {
  try {
    return sessionStorage.getItem(OFFER_KEY) === "1";
  } catch {
    return false;
  }
}

function dropOffer(): void {
  try {
    sessionStorage.removeItem(OFFER_KEY);
  } catch {
    /* см. выше */
  }
}

export function WelcomePage() {
  const { space, loading, refresh } = useSession();
  const t = useT();
  const navigate = useNavigate();

  const spaceId = space?.id ?? null;
  const mode = useEncryptionMode(spaceId);
  const { recheck } = useSpaceKey(spaceId);

  /*
   * Отметка читается при первом рендере и стирается сразу, как только экран
   * появился на свет. Дальше решает состояние компонента: перезагрузка начнёт с
   * пустого хранилища и уедет в списки, а отказавшийся сюда уже не вернётся —
   * возвращать нечего.
   */
  const [offered] = useState(claimOffer);
  useEffect(dropOffer, []);

  const [stage, setStage] = useState<Stage>("offer");
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteFailed, setInviteFailed] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  /*
   * Пока сессия не ответила, «пространства нет» — это незнание, а не ответ.
   *
   * Этот экран стоит вне `AppLayout` намеренно (см. App.tsx), а вместе с ним —
   * и вне его ожидания сессии. Без этой строки свежая загрузка успевала бы
   * решить «не владелец» раньше, чем узнала бы, кто пришёл, и увела бы в списки,
   * потратив отметку: показать этот экран второй раз уже нечем.
   */
  if (loading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-ground text-muted">
        <span className="sr-only">{t("common.loading")}</span>
        <span aria-hidden="true" className="size-6 animate-pulse rounded-full bg-line" />
      </div>
    );
  }

  /*
   * Спрашивается только на первом шаге, и это не мелочь: после включения режим
   * станет `e2ee`, и тот же вопрос увёл бы человека с экрана с кодом — который
   * показывается один раз за всю жизнь пространства.
   */
  if (
    stage === "offer" &&
    (!offered || !space || mode !== "none" || space.role !== "owner")
  ) {
    return <Navigate to="/lists" replace />;
  }

  async function turnOn() {
    if (!spaceId || busy) return;

    setBusy(true);
    setFailure(null);

    let enabled;
    try {
      enabled = await enableEncryption(spaceId);
    } catch (cause) {
      setFailure(cause instanceof ApiError ? cause.message : t("encryption.enableFailed"));
      return;
    } finally {
      setBusy(false);
    }

    /*
     * Ровно тот же порядок, что и в карточке настроек, и по тем же причинам.
     *
     * Код на экране раньше всего остального: он существует один раз и нигде не
     * хранится, а сорвавшаяся следом сеть не имеет права его унести. Потом
     * сессия и состояние синхронизации: пока `syncEngine` считает пространство
     * открытым, он выдаёт и кладёт в кэш нешифрующий кодек. Обе неудачи здесь
     * молчаливые — следующая дельта сделает то же самое, а человек в этот момент
     * переписывает код.
     */
    setRecoveryCode(enabled.recoveryCode);
    setStage("code");

    await refresh().catch(() => undefined);
    await syncEngine.pull(spaceId);
    // Ключ только что лёг на устройство — иначе замок вокруг списков ещё считает,
    // что его здесь нет.
    recheck();
  }

  /**
   * «Я сохранил» — единственный выход с этого шага. Ни крестика, ни клика по
   * фону: случайно закрытый экран стоит человеку всей будущей переписки.
   *
   * Догоняющего прохода после него нет, и это намеренно: пространство завели
   * минуту назад, перешифровывать в нём нечего. В настройках проход есть — там
   * включение застаёт написанное за годы.
   */
  function acknowledge() {
    setRecoveryCode(null);
    void goToInvite();
  }

  /**
   * Последний шаг обеих дорог: позвать второго.
   *
   * Это приложение на двоих, в котором пока один, и единственное осмысленное
   * первое действие — не «создайте первый список», а «позовите её». Кнопка
   * «Пригласить» лежит четвёртым блоком настроек, под профилем, пространством
   * и участниками, — и о ней на первом экране не было сказано ни слова.
   *
   * Код выписывается прямо здесь. Отправить человека в настройки за ним значило
   * бы дать ему задание вместо действия.
   */
  async function goToInvite() {
    if (!spaceId) {
      navigate("/lists", { replace: true });
      return;
    }

    setStage("invite");
    setInviting(true);
    setInviteFailed(null);

    try {
      const { invite } = await api.createInvite(spaceId);
      setInviteCode(invite.code);
    } catch (cause) {
      // Не тупик: код всегда можно выписать в настройках, и экран это говорит.
      setInviteFailed(cause instanceof ApiError ? cause.message : t("settings.inviteFailed"));
    } finally {
      setInviting(false);
    }
  }

  async function copyInvite() {
    if (!inviteCode) return;
    const link = `${location.origin}/register?code=${encodeURIComponent(inviteCode)}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      window.prompt(t("encryption.copyManually"), link);
    }
  }

  async function copyCode() {
    if (!recoveryCode) return;
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
    } catch {
      // Буфер обмена закрыт вне жеста и на незащищённом источнике, а сам объект
      // на http отсутствует вовсе.
      window.prompt(t("encryption.copyManually"), recoveryCode);
    }
  }

  if (stage === "invite") {
    return (
      <AuthShell
        title={t("welcome.inviteTitle")}
        lede={t("welcome.inviteLede")}
        footer={t("welcome.inviteWhere")}
      >
        <div className="flex flex-col gap-3">
          {inviting ? (
            <p className="px-1 text-[0.9375rem] text-muted">{t("welcome.inviteMinting")}</p>
          ) : null}

          {inviteCode ? (
            <code
              aria-label={t("welcome.inviteCodeAria")}
              className={cn(
                "block rounded-row bg-surface px-4 py-4 text-center",
                "font-mono text-[1.25rem] tracking-[0.16em] select-all",
              )}
            >
              {inviteCode}
            </code>
          ) : null}

          {inviteFailed ? (
            <p role="alert" className="px-1 text-[0.875rem] text-neg">
              {inviteFailed}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            {inviteCode ? (
              <Button variant="secondary" full onClick={() => void copyInvite()}>
                <CopyIcon className="size-5" />
                {t("welcome.inviteCopy")}
              </Button>
            ) : null}
            <Button full onClick={() => navigate("/lists", { replace: true })}>
              {inviteCode ? t("welcome.inviteDone") : t("welcome.inviteLater")}
            </Button>
          </div>

          <span role="status" className="text-center text-[0.8125rem] text-muted">
            {copied ? t("welcome.inviteCopied") : ""}
          </span>
        </div>
      </AuthShell>
    );
  }

  if (stage === "code" && recoveryCode) {
    return (
      <AuthShell
        title={t("encryption.codeTitle")}
        lede={t("encryption.welcomeCodeLede")}
        footer={t("encryption.codeWhere")}
      >
        <div className="flex flex-col gap-3">
          {/* `select-all`: одно касание выделяет код целиком, и человеку не
              приходится ловить границы группы пальцем. */}
          <code
            aria-label={t("encryption.codeAria")}
            className={cn(
              "block rounded-row bg-surface px-4 py-4 text-center",
              "font-mono text-[1.0625rem] leading-relaxed tracking-[0.12em] break-all select-all",
            )}
          >
            {recoveryCode}
          </code>

          <p className="text-[0.875rem] text-ink-soft">{t("encryption.codeOnce")}</p>

          <div className="flex flex-col gap-2">
            <Button variant="secondary" full onClick={copyCode}>
              <CopyIcon className="size-5" />
              {t("encryption.copy")}
            </Button>
            <Button full onClick={acknowledge}>
              {t("encryption.codeSaved")}
            </Button>
          </div>

          <span role="status" className="text-center text-[0.8125rem] text-muted">
            {copied ? t("encryption.copied") : ""}
          </span>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("encryption.welcomeTitle")}
      lede={t("encryption.welcomeLede")}
      footer={t("encryption.welcomeLaterNote")}
    >
      <div className="flex flex-col gap-4">
        {/* Две половины — две одинаковые плашки: один тональный шаг от фона,
            один размер заголовка, один размер текста. Что угодно другое читалось
            бы как подсказка, какую половину выбрать. */}
        <div className="flex flex-col gap-3">
          <Half title={t("encryption.welcomeOffTitle")} body={t("encryption.welcomeOffBody")} />
          <Half title={t("encryption.welcomeOnTitle")} body={t("encryption.welcomeOnBody")} />
        </div>

        <p className="px-1 text-[0.8125rem] text-muted">{t("encryption.welcomeForever")}</p>

        {failure ? (
          <p role="alert" className="px-1 text-[0.875rem] text-neg">
            {failure}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <Button full onClick={() => void turnOn()} disabled={busy}>
            {busy ? t("encryption.enabling") : t("encryption.enable")}
          </Button>
          <Button
            variant="ghost"
            full
            disabled={busy}
            onClick={() => void goToInvite()}
          >
            {t("encryption.welcomeLater")}
          </Button>
        </div>
      </div>
    </AuthShell>
  );
}

function Half({ title, body }: { title: string; body: string }) {
  return (
    <Card className="px-4 py-3.5">
      <h2 className="font-display text-[0.9375rem]">{title}</h2>
      <p className="mt-1.5 text-[0.875rem] text-ink-soft">{body}</p>
    </Card>
  );
}
